/**
 * Custom Sticky Add-to-Cart Bar
 *
 * Shows a fixed add-to-cart bar at the bottom of product pages
 * when the main product form scrolls out of the viewport.
 *
 * Features:
 * - Variant selector and quantity stepper in the sticky bar
 * - Bidirectional sync with main product form
 * - Price updates from embedded variant JSON data
 * - Submits to cart via Shopify's cart API
 * - Updates cart drawer/notification on success
 * - Accessible with ARIA live regions for loading states
 */

(function () {
  'use strict';

  const SELECTORS = {
    stickyBar: '[data-custom-sticky-atc]',
    stickyButton: '[data-custom-sticky-atc-button]',
    stickyButtonText: '[data-custom-sticky-atc-button-text]',
    stickySelect: '[data-custom-sticky-atc-select]',
    stickyPrice: '[data-custom-sticky-atc-price]',
    stickyQtyInput: '[data-custom-sticky-atc-qty-input]',
    stickyQtyMinus: '[data-custom-sticky-atc-qty-minus]',
    stickyQtyPlus: '[data-custom-sticky-atc-qty-plus]',
    variantsJson: '[data-custom-sticky-atc-variants]',
    mainProductForm: 'product-info[id^="MainProduct"] form[data-type="add-to-cart-form"]',
    mainVariantInput: '.product-variant-id',
    mainSubmitButton: '[type="submit"][name="add"]',
  };

  const CLASSES = {
    visible: 'custom-sticky-atc--visible',
    loading: 'custom-sticky-atc__button--loading',
  };

  class NSStickyATC {
    constructor() {
      this.stickyBar = document.querySelector(SELECTORS.stickyBar);
      if (!this.stickyBar) return;

      this.stickyButton = this.stickyBar.querySelector(SELECTORS.stickyButton);
      this.stickyButtonText = this.stickyBar.querySelector(SELECTORS.stickyButtonText);
      this.stickySelect = this.stickyBar.querySelector(SELECTORS.stickySelect);
      this.stickyPrice = this.stickyBar.querySelector(SELECTORS.stickyPrice);
      this.stickyQtyInput = this.stickyBar.querySelector(SELECTORS.stickyQtyInput);
      this.stickyQtyMinus = this.stickyBar.querySelector(SELECTORS.stickyQtyMinus);
      this.stickyQtyPlus = this.stickyBar.querySelector(SELECTORS.stickyQtyPlus);
      this.mainProductForm = document.querySelector(SELECTORS.mainProductForm);

      if (!this.mainProductForm || !this.stickyButton) return;

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.isLoading = false;
      this.syncingFromMain = false;
      this.syncingToMain = false;

      this.loadVariantData();
      this.init();
    }

    loadVariantData() {
      const jsonEl = document.querySelector(SELECTORS.variantsJson);
      try {
        this.variants = jsonEl ? JSON.parse(jsonEl.textContent) : [];
      } catch {
        this.variants = [];
      }
    }

    init() {
      this.setupScrollDetection();
      this.setupVariantSync();
      this.setupVariantSelect();
      this.setupQuantity();
      this.setupAddToCart();
    }

    /**
     * Scroll-based detection for when the main product form leaves the viewport.
     */
    setupScrollDetection() {
      const buyButtonWrapper = document.querySelector('.product-buy__button');
      const target = buyButtonWrapper || this.mainProductForm;

      let ticking = false;
      const check = () => {
        const rect = target.getBoundingClientRect();
        if (rect.bottom < 0) {
          this.show();
        } else {
          this.hide();
        }
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(check);
          ticking = true;
        }
      }, { passive: true });

      check();
    }

    /**
     * Listen for main form variant changes (main → sticky sync)
     */
    setupVariantSync() {
      const mainVariantInput = this.mainProductForm.querySelector(SELECTORS.mainVariantInput);
      if (!mainVariantInput) return;

      // Observe the variant input for value changes
      const variantObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            this.syncFromMainForm();
          }
        });
      });

      variantObserver.observe(mainVariantInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled'],
      });

      // Listen for variant picker radio/select changes
      document.addEventListener('change', (event) => {
        if (event.target.closest('.product-form__input') && !this.syncingToMain) {
          setTimeout(() => this.syncFromMainForm(), 50);
        }
      });
    }

    /**
     * Sync state from main product form → sticky bar
     */
    syncFromMainForm() {
      if (this.syncingToMain) return;
      this.syncingFromMain = true;

      const mainVariantInput = this.mainProductForm.querySelector(SELECTORS.mainVariantInput);
      const mainSubmitButton = this.mainProductForm.querySelector(SELECTORS.mainSubmitButton);

      if (!mainVariantInput) {
        this.syncingFromMain = false;
        return;
      }

      const variantId = mainVariantInput.value;

      // Update sticky select to match main form
      if (this.stickySelect && variantId) {
        this.stickySelect.value = variantId;
      }

      // Update button state
      const isDisabled = mainVariantInput.disabled || mainSubmitButton?.disabled;
      this.stickyButton.disabled = isDisabled;

      // Update button text
      if (mainSubmitButton) {
        const mainButtonText = mainSubmitButton.querySelector('span')?.textContent?.trim();
        if (mainButtonText && this.stickyButtonText) {
          this.stickyButtonText.textContent = mainButtonText;
        }
      }

      // Update price from variant data
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (variant) {
        this.updatePrice(variant);
        this.updateImage(variant);
      }

      this.syncingFromMain = false;
    }

    /**
     * Setup variant select change handler (sticky → main sync)
     */
    setupVariantSelect() {
      if (!this.stickySelect) return;

      this.stickySelect.addEventListener('change', () => {
        const variantId = this.stickySelect.value;
        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;

        // Update sticky bar UI
        this.updatePrice(variant);
        this.updateButtonState(variant);
        this.updateImage(variant);

        // Sync to main form
        this.syncToMainForm(variantId);
      });
    }

    /**
     * Sync sticky bar selection → main product form
     */
    syncToMainForm(variantId) {
      this.syncingToMain = true;

      const mainVariantInput = this.mainProductForm.querySelector(SELECTORS.mainVariantInput);
      if (mainVariantInput) {
        mainVariantInput.value = variantId;
        mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Try to update the URL with the variant ID
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);

      // Trigger Dawn's variant change system by dispatching on variant picker inputs.
      // Dawn uses fieldsets with radio buttons or selects per option (e.g., Size, Color).
      // We match the variant by checking against variant title parts.
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (variant) {
        const titleParts = variant.title.split(' / ');
        const optionFieldsets = document.querySelectorAll('.product-form__input');

        optionFieldsets.forEach((fieldset, index) => {
          if (index >= titleParts.length) return;
          const value = titleParts[index].trim();

          // Try radio buttons first
          const radio = fieldset.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          // Try select dropdown
          const select = fieldset.querySelector('select');
          if (select) {
            for (const option of select.options) {
              if (option.textContent.trim() === value) {
                select.value = option.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                break;
              }
            }
          }
        });
      }

      // Reset flag after Dawn has had time to process
      setTimeout(() => {
        this.syncingToMain = false;
      }, 100);
    }

    /**
     * Update price display from variant data
     */
    updatePrice(variant) {
      if (!this.stickyPrice) return;

      const formatMoney = (cents) => {
        // Use Shopify's money format if available, otherwise simple format
        if (window.Shopify && window.Shopify.formatMoney) {
          return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        }
        const amount = (cents / 100).toFixed(2);
        return `$${amount}`;
      };

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.stickyPrice.innerHTML =
          `<span class="custom-sticky-atc__price-sale">${formatMoney(variant.price)}</span>` +
          `<span class="custom-sticky-atc__price-compare"><s>${formatMoney(variant.compare_at_price)}</s></span>`;
      } else {
        this.stickyPrice.innerHTML =
          `<span class="custom-sticky-atc__price-regular">${formatMoney(variant.price)}</span>`;
      }
    }

    /**
     * Update product image when variant has a featured image
     */
    updateImage(variant) {
      if (!variant.featured_image) return;
      const img = this.stickyBar.querySelector('.custom-sticky-atc__image img');
      if (img) {
        img.src = variant.featured_image;
      }
    }

    /**
     * Update button state based on variant availability
     */
    updateButtonState(variant) {
      this.stickyButton.disabled = !variant.available;
      if (this.stickyButtonText) {
        this.stickyButtonText.textContent = variant.available
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    /**
     * Setup quantity stepper controls
     */
    setupQuantity() {
      if (!this.stickyQtyInput) return;

      this.stickyQtyMinus?.addEventListener('click', () => {
        const current = parseInt(this.stickyQtyInput.value, 10) || 1;
        if (current > 1) {
          this.stickyQtyInput.value = current - 1;
        }
      });

      this.stickyQtyPlus?.addEventListener('click', () => {
        const current = parseInt(this.stickyQtyInput.value, 10) || 1;
        this.stickyQtyInput.value = current + 1;
      });

      // Sanitize manual input
      this.stickyQtyInput.addEventListener('change', () => {
        const val = parseInt(this.stickyQtyInput.value, 10);
        if (!val || val < 1) {
          this.stickyQtyInput.value = 1;
        }
      });
    }

    /**
     * Setup add to cart functionality
     */
    setupAddToCart() {
      this.stickyButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.addToCart();
      });
    }

    /**
     * Get the currently selected variant ID from sticky bar controls
     */
    getVariantId() {
      // Use sticky select if available (multi-variant product)
      if (this.stickySelect) {
        return this.stickySelect.value;
      }
      // Fall back to main form's variant input (single-variant product)
      const mainVariantInput = this.mainProductForm.querySelector(SELECTORS.mainVariantInput);
      return mainVariantInput?.value;
    }

    /**
     * Get quantity from sticky bar's own input
     */
    getQuantity() {
      if (this.stickyQtyInput) {
        return parseInt(this.stickyQtyInput.value, 10) || 1;
      }
      return 1;
    }

    /**
     * Add to cart via Shopify cart API
     */
    async addToCart() {
      if (this.isLoading) return;

      const variantId = this.getVariantId();
      if (!variantId) return;

      // Check if selected variant is available
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (variant && !variant.available) return;

      this.setLoading(true);

      try {
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        };

        const formData = {
          id: variantId,
          quantity: this.getQuantity(),
        };

        if (this.cart) {
          formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(formData);

        const response = await fetch(window.routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          this.handleError(data.description || data.message);
          return;
        }

        if (this.cart) {
          this.cart.renderContents(data);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (error) {
        console.error('[custom-sticky-atc] Add to cart failed', error);
        this.handleError('Failed to add to cart. Please try again.');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Handle add to cart error
     */
    handleError(message) {
      const errorWrapper = document.querySelector('.product-form__error-message-wrapper');
      const errorMessage = document.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorMessage.textContent = message;
        errorWrapper.removeAttribute('hidden');
        setTimeout(() => {
          errorWrapper.setAttribute('hidden', '');
        }, 5000);
      } else {
        alert(message);
      }
    }

    /**
     * Set loading state
     */
    setLoading(loading) {
      this.isLoading = loading;
      this.stickyButton.classList.toggle(CLASSES.loading, loading);
      this.stickyButton.setAttribute('aria-disabled', loading);
    }

    show() {
      this.stickyBar.classList.add(CLASSES.visible);
    }

    hide() {
      this.stickyBar.classList.remove(CLASSES.visible);
    }
  }

  function init() {
    new NSStickyATC();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
